import { get, isArray, isObject, isString, pick } from 'lodash-es';
import { useObservable, useObservableState } from 'observable-hooks';
import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { FieldPath, FormState, UseFormReturn, UseFormWatch } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import type { Observable } from 'rxjs';
import {
    defer,
    distinctUntilChanged,
    fromEventPattern,
    identity,
    map,
    startWith,
    switchMap,
} from 'rxjs';

import { useDeepCompareMemoize } from '../../hooks/useDeepCompareMemoize';
import type { SwarmFormController } from '../pages/measuringPoint/form-controller';
import type { FieldValues, RhfFormControl } from './types';

interface FormContextProps {
    formController: SwarmFormController;
}

export const OVERRIDES_NAME = 'overrides';

// This is our own form context. Not the `useFormContext` from react-form-hook.
export const FormContext = createContext<FormContextProps | null>(null);

export function useSharedFormContext() {
    const context = useContext(FormContext);

    if (!context) {
        throw new Error('No shared form context available.');
    }

    return context;
}

interface FormFieldContextProps {
    hover: boolean;
    setHover: (isHovering: boolean) => void;
}

export const FormFieldContext = createContext<FormFieldContextProps | null>(null);

export const defaultPropTypes = {
    fieldOptions: PropTypes.shape({
        name: PropTypes.string.isRequired,
        settings: PropTypes.object,
    }).isRequired,
};

export function useOverrideState<T extends FieldValues = FieldValues>(
    name: string
): {
    overridable: boolean;
    overridden: boolean;
    disabled: boolean;
} {
    const { formController } = useSharedFormContext();
    const overridable = useObservableState(
        useObservable(() => formController.isOverridable$(name)),
        false
    );
    const { watch, getValues } = useFormContext<T>();
    const getOverriddenValue = useCallback(
        () => getValues(`${OVERRIDES_NAME}.${name}` as FieldPath<T>) as boolean,
        [name, getValues]
    );
    const [overridden, setOverridden] = useState(getOverriddenValue() || false);

    useEffect(() => {
        const subscription = watch((_values, { name: nameOfUpdated }) => {
            if (nameOfUpdated === `${OVERRIDES_NAME}.${name}`) {
                setOverridden(getOverriddenValue());
            }
        });

        return subscription.unsubscribe;
    }, [watch, getOverriddenValue, name]);

    return {
        overridable,
        overridden,
        disabled: overridable && !overridden,
    };
}

type Fields<T> = Map<string, T> | Record<string, T>;

type NestedFields<T> = T | Fields<T | Fields<T>>;

/**
 * Converts a structure like:
 * {
 *     logFlushInterval: false,
 *     dustSampleTime: true,
 *     notificationConfig: {
 *         leqAlarms: false,
 *         emailDelay: true,
 *     },
 * }
 * Into an array of enabled field paths:
 * [
 *     'dustSampleTime',
 *     'notificationConfig.emailDelay'
 * ]
 */
export function extractEnabledFields<T extends FieldValues = FieldValues>(
    fields: NestedFields<T>,
    parentPath = ''
): Array<string> {
    let entries: Array<[string, NestedFields<T>]> = [];

    if (fields instanceof Map) {
        entries = Array.from(fields);
    } else if (isObject(fields)) {
        entries = Object.entries(fields) as Array<[string, NestedFields<T>]>;
    }

    return entries.flatMap(([key, value]) => {
        const currentPath = parentPath ? `${parentPath}.${key}` : key;

        if (['notificationConfig', 'schedule'].includes(key)) {
            // Recursively process nested objects.
            return extractEnabledFields(value, currentPath);
        }

        // Return the path if the value is truthy, otherwise empty array.
        return value ? [currentPath] : [];
    });
}

export function useWatchNestedField<
    T extends FieldPath<TFieldValues>,
    Value extends Record<string, unknown> = Record<string, unknown>,
    TFieldValues extends FieldValues = FieldValues
>(watch: UseFormWatch<TFieldValues>, field: T, defaultValue: Value): Value {
    const [fieldValue, setFieldValue] = useState(defaultValue);

    useEffect(() => {
        const subscription = watch((values, { name }) => {
            if (name && name.startsWith(field)) {
                setFieldValue(() => ({ ...(values[field] as Value) }));
            }
        });
        return subscription.unsubscribe;
    }, [watch, field]);

    return fieldValue;
}

/**
 * Represents the shape of the object emitted by React Hook Form's `control.subscribe` method.
 * It includes all standard FormState properties, plus the current form `values`.
 */
type RHFSubscriptionValue<TFieldValues extends FieldValues = FieldValues> =
    FormState<TFieldValues> & {
        values: TFieldValues;
    };

// Disabling `no-underscore-dangle` as we are intentionally accessing internal
// React Hook Form properties for state snapshotting, which is not something we control.
/* eslint-disable no-underscore-dangle */

/**
 * Creates an observable from React Hook Form's internal `formControl` object.
 * This is an advanced utility for observing the entire form state or a subset of it.
 *
 * It provides an initial emission of the current state upon subscription
 * (unless in `lazy` mode), followed by subsequent updates.
 *
 * @template TFieldValues The type of the form's field values.
 * @param {RhfFormControl} formControl The `formControl` object from `useFormContext` or
 * `formContext` object from `useForm`.
 * @param {string|string[]|null} [property] The specific property or properties to observe from
 * the combined form state and values (e.g., 'isDirty', 'values', or ['isDirty', 'isValid']).
 * If omitted, the entire combined state object is returned.
 * @param {boolean} [lazy=false] If `true`, the observable only emits when form
 * state changes. If `false` (default), it immediately emits the current state upon subscription.
 * @returns {Observable<unknown>} An observable of the form state or a specific property/subset of
 * it. The emitted type is dynamic, so `unknown` is used for type safety, requiring the consumer
 * to perform type checks.
 */
export function fromReactHookFormControl<
    TFieldValues extends FieldValues = FieldValues,
    TContext = unknown
>(
    formControl: UseFormReturn<TFieldValues> | RhfFormControl<TFieldValues, TContext>,
    property:
        | keyof RHFSubscriptionValue<TFieldValues>
        | (keyof RHFSubscriptionValue<TFieldValues>)[]
        | null = null,
    lazy: boolean = false
): Observable<unknown> {
    // `defer` ensures the entire setup logic is executed lazily for each new subscriber,
    // making sure the `initialState` is fresh at the moment of subscription.
    return defer(() => {
        // The initial state is captured at the moment of subscription for the non-lazy path.
        const initialState: RHFSubscriptionValue<TFieldValues> = {
            values: { ...formControl.control._formValues },
            ...formControl.control._formState,
        } as RHFSubscriptionValue<TFieldValues>;

        // `fromEventPattern` is used to wrap the `subscribe` method.
        const changes$ = fromEventPattern<RHFSubscriptionValue<TFieldValues>>(
            (handler) => {
                let propertyAsArray: (keyof RHFSubscriptionValue<TFieldValues>)[] = [];
                if (isArray(property)) {
                    propertyAsArray = property;
                } else if (property) {
                    propertyAsArray = [property];
                }

                const fieldsToWatch = Object.fromEntries(
                    propertyAsArray.map((p) => [p, true])
                ) as Record<string, boolean>;

                return formControl.subscribe({ formState: fieldsToWatch, callback: handler });
            },
            (_handler, unsubscribe) => (unsubscribe as () => void)()
        );

        // Create the final observable by piping a chain of operators.
        return changes$.pipe(
            // If not in lazy mode, `startWith` prepends the current state to the stream,
            // ensuring an immediate emission. Otherwise, `identity` acts as a no-op.
            lazy ? identity : startWith(initialState),
            // This `map` operator is responsible for shaping the data into its final desired form
            // based on the `property` argument.
            map((formStateAndValues) => {
                if (isString(property)) {
                    // If `property` is a string, pluck that single value from the combined state.
                    return get(formStateAndValues, property);
                }
                if (isArray(property)) {
                    // If `property` is an array, return an object containing only those keys.
                    return pick(formStateAndValues, property);
                }
                // If `property` is null or undefined, return the whole combined state object.
                return formStateAndValues;
            })
        );
    });
}
/* eslint-enable no-underscore-dangle */

// A conditional type that calculates the observable's emission type based on the
// `property` argument.
type UseFormStateObservableReturn<TProperty, TFieldValues extends FieldValues> = TProperty extends
    | null
    | undefined
    ? // If property is null, return the full object.
      RHFSubscriptionValue<TFieldValues>
    : TProperty extends keyof RHFSubscriptionValue<TFieldValues>
    ? // If property is a single key, return the type of that key's value.
      RHFSubscriptionValue<TFieldValues>[TProperty]
    : TProperty extends (keyof RHFSubscriptionValue<TFieldValues>)[]
    ? // If property is an array of keys, return a picked object.
      Pick<RHFSubscriptionValue<TFieldValues>, TProperty[number]>
    : unknown; // Fallback for any other case.

/**
 * Creates an efficient, memoized observable for a specific property or a subset
 * of React Hook Form's state (including form values).
 *
 * @template TFieldValues The type of the form's field values.
 * @param {string|string[]|null} [property] The specific property or properties to
 * observe (e.g., 'isDirty', 'values', or ['isDirty', 'isValid']). If omitted, the entire
 * combined state object is returned.
 * @returns {Observable} An observable that emits the requested part of the form state whenever
 * it changes.
 */
export function useFormStateObservable<TFieldValues extends FieldValues = FieldValues>(
    property:
        | keyof RHFSubscriptionValue<TFieldValues>
        | (keyof RHFSubscriptionValue<TFieldValues>)[]
        | null = null
): Observable<UseFormStateObservableReturn<typeof property, TFieldValues>> {
    const { formController } = useSharedFormContext();

    // The `property` array/object can be a new reference on every render, which
    // would cause `useObservable` to resubscribe unnecessarily. `useDeepCompareMemoize`
    // provides a referentially stable version of `property` as long as its contents
    // do not change deeply.
    const stableProperty = useDeepCompareMemoize(property);

    // `useObservable` creates and subscribes to our observable stream.
    // All complex logic for watching form state is neatly encapsulated in
    // `fromReactHookFormControl`. The stream is only re-created if `formControl`
    // or the content of `property` (via `stableProperty`) changes.
    const formState$ = useObservable(
        (inputs$) => inputs$.pipe(switchMap(([fc, prop]) => fromReactHookFormControl(fc, prop))),
        [formController.rhfProps.formControl, stableProperty]
    );

    // We use a type assertion (`as`) here as a deliberate bridge. We guarantee that the
    // `Observable<unknown>` from our general-purpose utility is, in fact, compatible
    // with the more specific, "smart" type that this hook promises to its consumers.
    return formState$ as Observable<UseFormStateObservableReturn<typeof property, TFieldValues>>;
}

export function fromReactHookFormWatch<
    TFieldValues extends FieldValues = FieldValues,
    TContext = unknown
>(
    formControl: UseFormReturn<TFieldValues> | RhfFormControl<TFieldValues, TContext>,
    // This direct union type is simpler and fixes the default value error.
    field: FieldPath<TFieldValues> | FieldPath<TFieldValues>[] | null = null,
    lazy = false
): Observable<unknown> {
    // Returning `unknown` is safe and avoids the `any` linting error.
    // This utility starts by observing the entire `values` object from the form state.
    return fromReactHookFormControl(formControl, 'values', lazy).pipe(
        map((values) => {
            // Because `fromReactHookFormControl` returns `unknown`, we can assert the type
            // of `values` here to use it safely inside this `map` operator.
            const typedValues = values as TFieldValues;

            if (field == null) {
                return typedValues;
            }
            if (isArray(field)) {
                return pick(typedValues, field);
            }
            // TODO(omnidots/website#8673): Replace with a typed accessor once available.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return get(typedValues, field);
        })
    );
}

/**
 * Custom hook to create an observable for React Hook Form's `watch` functionality.
 * @param {string | string[] | null} [field=null] The field name(s) to watch.
 * Pass null or omit to watch all fields.
 * @param {boolean} [lazy=true] Determines the initial emission behavior.
 * If `true` (default), the observable only emits when form values change after subscription.
 * If `false`, the observable immediately emits the current form values (using `getValues()`)
 * upon subscription, followed by subsequent changes.
 * @returns {Observable<any>} An observable of the watched form values.
 */
export function useWatchObservable<
    Value = unknown,
    Field extends FieldPath<FieldValues> = keyof FieldValues
>(field: Field | null = null, lazy = false) {
    const { formController } = useSharedFormContext();

    return useObservable(
        (inputs$) =>
            inputs$.pipe(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                switchMap(([fc]) => fromReactHookFormWatch(fc, field, lazy)),
                distinctUntilChanged()
            ),
        [formController.rhfProps.formControl]
    ) as unknown as Observable<Value>;
}
